import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Section, Reveal, Overline } from "./primitives";
import { PORTFOLIO, PORTFOLIO_CATS } from "../../data";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export default function Portfolio() {
  const [cat, setCat] = useState("All");
  const navigate = useNavigate();
  const items = cat === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === cat);

  return (
    <Section id="portfolio" className="noise-bg">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <Overline>Selected Work</Overline>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight">
              Work that <span className="text-gradient">performs</span>.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {PORTFOLIO_CATS.map((c) => (
              <button
                key={c}
                data-testid={`portfolio-filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${cat === c ? "bg-brand-gradient text-white border-transparent" : "border-border/15 text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      <LayoutGroup>
        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.div
                layout
                key={p.title}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => navigate(`/work/${p.slug}`)}
                data-testid={`portfolio-tile-${p.slug}`}
                className="mb-5 break-inside-avoid group relative rounded-3xl overflow-hidden border border-border/10 cursor-pointer"
              >
                <img src={p.img} alt={p.title} className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${p.tall ? "h-[28rem]" : "h-64"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-90" />
                <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">{p.cat}</span>
                    <h3 className="mt-1 font-display text-xl font-bold text-white">{p.title}</h3>
                    <span className="mt-1 inline-block text-xs text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">View case study</span>
                  </div>
                  <div className="h-10 w-10 grid place-items-center rounded-full bg-white/15 backdrop-blur border border-white/30 text-white translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                    <ArrowUpRight className="h-5 w-5" />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </LayoutGroup>
    </Section>
  );
}
